<template>
  <div
    class="multi-counter-design-root"
    :class="{ 'dir-row': direction === 'row', 'dir-column': direction === 'column' }"
    :style="{ fontFamily: cssFontFamily }"
  >
    <div
      v-for="counter in counters"
      :key="counter.id"
      class="multi-counter-design-item"
      :class="`mcd-variant-${shapeVariantOf(counter)}`"
      :style="[itemStyle(counter), { color: textColorOf(counter) }]"
    >
      <div v-if="counter.label" class="multi-counter-design-header">
        <span class="multi-counter-design-label">
          <span class="multi-counter-design-deco" aria-hidden="true"></span>
          <span class="multi-counter-design-label-text">{{ counter.label }}</span>
          <span class="multi-counter-design-deco" aria-hidden="true"></span>
        </span>
        <div class="multi-counter-design-divider"></div>
      </div>
      <div class="multi-counter-design-value">
        <span class="multi-counter-design-count"
          >{{ counter.count }}<span
            v-if="counter.unit"
            class="multi-counter-design-unit"
          >{{ counter.unit }}</span></span>
      </div>
    </div>
  </div>
</template>

<script>
import { buildItemStyle, shapeVariant } from '../utils/multiCounterThemes'

const STORAGE_KEY = 'multiCounters'
const THEME_KEY = 'multiCounterTheme'

const DEFAULT_THEME = {
  themeBackgroundShape: 'rounded',
  themeBackgroundColor: 'sakura',
  themeTextColor: '#8B4C63'
}

function migrateCounter(c, theme) {
  if (c && c.useTheme === true && theme) {
    const migrated = {
      ...c,
      backgroundShape: theme.themeBackgroundShape,
      backgroundColor: theme.themeBackgroundColor,
      textColor: theme.themeTextColor
    }
    delete migrated.useTheme
    return migrated
  }
  return c
}

function decodeCfg(cfg) {
  if (!cfg || typeof cfg !== 'string') return null
  // Accept base64url (new) or plain base64 (older links). Restore padding
  // and revert the URL-safe substitutions before atob.
  const padded = cfg.replace(/-/g, '+').replace(/_/g, '/')
  const padLen = (4 - (padded.length % 4)) % 4
  try {
    const json = decodeURIComponent(escape(atob(padded + '='.repeat(padLen))))
    const parsed = JSON.parse(json)
    if (!Array.isArray(parsed)) return null
    // Normalize short-key entries (`i/l/u/s/c/t`) back to full property names
    // while still accepting the older long-key format for backward compat.
    return parsed.map(entry => {
      if (!entry || typeof entry !== 'object') return entry
      return {
        id: entry.id != null ? entry.id : entry.i,
        label: entry.label != null ? entry.label : entry.l,
        unit: entry.unit != null ? entry.unit : entry.u,
        backgroundShape: entry.backgroundShape != null ? entry.backgroundShape : entry.s,
        backgroundColor: entry.backgroundColor != null ? entry.backgroundColor : entry.c,
        textColor: entry.textColor != null ? entry.textColor : entry.t
      }
    })
  } catch (e) {
    return null
  }
}

function loadCounters(theme, cfgList) {
  let stored = []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) stored = parsed
    }
  } catch (e) {
    stored = []
  }

  // If URL cfg is present, use it as the authoritative counter set + design.
  // Live fields (count, label, unit) are merged in by id from the dock's
  // localStorage so updates from the OBS control dock still reflect.
  if (cfgList && cfgList.length > 0) {
    const liveById = {}
    stored.forEach(c => {
      if (c && c.id != null) liveById[c.id] = c
    })
    return cfgList.map(c => {
      const live = liveById[c.id]
      const merged = {
        count: 0,
        ...c,
        ...(live ? {
          count: typeof live.count === 'number' ? live.count : 0,
          label: typeof live.label === 'string' ? live.label : c.label,
          unit: typeof live.unit === 'string' ? live.unit : c.unit
        } : {})
      }
      return migrateCounter(merged, theme)
    })
  }

  return stored.map(c => migrateCounter(c, theme))
}

function loadTheme() {
  try {
    const raw = localStorage.getItem(THEME_KEY)
    if (!raw) return { ...DEFAULT_THEME }
    const parsed = JSON.parse(raw)
    return { ...DEFAULT_THEME, ...parsed }
  } catch (e) {
    return { ...DEFAULT_THEME }
  }
}

export default {
  props: {
    font: {
      type: String,
      default: "'Hachi Maru Pop', cursive"
    },
    dir: {
      type: String,
      default: 'row'
    },
    cfg: {
      type: String,
      default: ''
    }
  },
  data() {
    const theme = loadTheme()
    const cfgList = decodeCfg(this.cfg)
    return {
      counters: loadCounters(theme, cfgList),
      cfgList,
      theme,
      selectedFont: this.font || "'Hachi Maru Pop', cursive",
      direction: this.dir === 'column' ? 'column' : 'row'
    }
  },
  computed: {
    // Append a Japanese system-font fallback chain so kanji/kana don't
    // collapse to a generic ugly fallback when a Latin-only font is picked.
    cssFontFamily() {
      const fallback = "'Hiragino Sans','Hiragino Kaku Gothic ProN','Yu Gothic',YuGothic,'Meiryo',sans-serif"
      return `${this.selectedFont},${fallback}`
    }
  },
  mounted() {
    window.addEventListener('storage', this.handleStorageChange)
    // Propagate the cfg-derived counter set to localStorage so the OBS
    // control dock (sharing the same CEF storage) can see and operate on
    // every counter — not just the one it seeded for itself.
    if (this.cfgList && this.cfgList.length > 0) {
      this.persistCounters()
    }
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    persistCounters() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.counters))
      } catch (e) {
        // Storage may be unavailable (private mode, quota); render still works.
      }
    },
    handleStorageChange(event) {
      if (event.key === STORAGE_KEY) {
        this.counters = loadCounters(this.theme, this.cfgList)
      } else if (event.key === THEME_KEY) {
        this.theme = loadTheme()
      }
    },
    itemStyle(counter) {
      return buildItemStyle(counter.backgroundColor, counter.backgroundShape)
    },
    textColorOf(counter) {
      return counter.textColor
    },
    shapeVariantOf(counter) {
      return shapeVariant(counter.backgroundShape)
    }
  }
}
</script>

<style>
.multi-counter-design-root {
  display: flex;
  width: 100%;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 0;
  box-sizing: border-box;
}

.multi-counter-design-root.dir-row {
  flex-direction: row;
  flex-wrap: wrap;
}

.multi-counter-design-root.dir-column {
  flex-direction: column;
}

.multi-counter-design-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  min-height: 220px;
  padding: 24px 28px;
  box-sizing: border-box;
}

.multi-counter-design-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  width: 100%;
}

.multi-counter-design-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 1.3rem;
  font-weight: bold;
  letter-spacing: 0.05em;
  line-height: 1.3;
  font-feature-settings: "palt" 1;
  text-rendering: optimizeLegibility;
}

.multi-counter-design-deco {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: var(--mc-accent, currentColor);
  transform: rotate(45deg);
  opacity: 0.9;
  flex: 0 0 auto;
}

.multi-counter-design-label-text {
  white-space: nowrap;
}

.multi-counter-design-divider {
  width: 65%;
  max-width: 160px;
  height: 1px;
  background: var(--mc-accent, currentColor);
  opacity: 0.5;
}

.multi-counter-design-value {
  text-align: center;
  line-height: 1;
}

.multi-counter-design-count {
  display: inline-block;
  position: relative;
  font-size: 4.5rem;
  line-height: 1;
  font-weight: bold;
}

.multi-counter-design-unit {
  position: absolute;
  left: 100%;
  bottom: 0.18em;
  margin-left: 6px;
  font-size: 1.6rem;
  line-height: 1;
  opacity: 0.75;
  font-weight: normal;
  white-space: nowrap;
}

/* ---- Bar variant (wide & short, with floating label badge) ---- */
.multi-counter-design-item.mcd-variant-bar {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: auto;
  padding: 22px 16px 14px;
  gap: 0;
  overflow: visible;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-header {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  margin-bottom: 0;
  gap: 0;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-label {
  background: var(--mc-label-bg, currentColor);
  color: var(--mc-label-color, #fff);
  padding: 5px 16px;
  border-radius: 999px;
  font-size: 1rem;
  letter-spacing: 0.03em;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14);
  white-space: nowrap;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-deco {
  background: var(--mc-label-color, #fff);
  opacity: 0.85;
  width: 6px;
  height: 6px;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-divider {
  display: none;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-value {
  flex: 0 0 auto;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-count {
  font-size: 3.2rem;
}

.multi-counter-design-item.mcd-variant-bar .multi-counter-design-unit {
  font-size: 1.2rem;
  bottom: 0.16em;
  margin-left: 4px;
}
</style>
